/** Decides when text typed into search is a question for QuantisAI. Pure, so web and native route alike. */

// Same cap as the QuantisAI composer, so a handed-over question is never trimmed twice.
export const ASK_PROMPT_LIMIT = 4000;

// Words that also name things people search for ("Will", "May", "draft invoices", "review meeting") are left out.
const QUESTION_STARTERS = new Set([
  'what', 'whats', "what's", 'how', 'why', 'when', 'where', 'which', 'who', 'whose', 'whom',
  'can', 'could', 'should', 'would', 'shall', 'is', 'are', 'was', 'were',
  'do', 'does', 'did', 'has', 'have', 'had',
  'explain', 'summarise', 'summarize', 'compare', 'calculate', 'estimate', 'suggest',
]);
const PHRASE_STARTERS = new Set(['show me', 'tell me', 'give me', 'help me', 'find me', 'list all']);
// A sentence this long is prose, not a name or reference someone is looking up.
const SENTENCE_WORDS = 6;

const words = (text: string) => text.trim().split(/\s+/).filter(Boolean);

export function isQuestionLike(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 2) return false;
  if (trimmed.endsWith('?')) return true;
  const parts = words(trimmed);
  if (parts.length >= SENTENCE_WORDS) return true;
  if (parts.length < 2) return false;
  const [first, second] = parts.map(part => part.toLocaleLowerCase().replace(/[^a-z']/g, ''));
  return QUESTION_STARTERS.has(first) || (parts.length >= 3 && PHRASE_STARTERS.has(`${first} ${second}`));
}

export function askPromptFrom(text: string): string {
  return text.trim().slice(0, ASK_PROMPT_LIMIT);
}

export type SearchEnterAction = 'ask' | 'first-result' | 'submit-search' | 'none';

/** What Enter does when no row is highlighted. Cmd/Ctrl+Enter always asks. */
export function searchEnterAction({ query, askAvailable, forceAsk = false, hasResults, navigationSearch }: {
  query: string;
  askAvailable: boolean;
  forceAsk?: boolean;
  hasResults: boolean;
  navigationSearch: boolean;
}): SearchEnterAction {
  const canAsk = askAvailable && !!askPromptFrom(query);
  if (canAsk && (forceAsk || isQuestionLike(query))) return 'ask';
  if (hasResults) return 'first-result';
  return navigationSearch ? 'none' : 'submit-search';
}
